import PropTypes from "prop-types";
import React from "react";
import { Alert, Button, Modal } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { provisionedSubscriptionShape } from "./provisionedSubscriptionShape";
import * as styles from "./ProvisioningStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "provisioning.renew.title",
		defaultMessage: "Extend subscription {number}",
	},
	ariaLabel: {
		id: "provisioning.renew.ariaLabel",
		defaultMessage: "Extend a provisioned subscription with one billing term",
	},
	body: {
		id: "provisioning.renew.body",
		defaultMessage: "This extends the subscription for {email} by one subscription period, counted from its current end date, or from today if it has already expired. The length of the period depends on the product. The customer keeps access to the plugins and courses in the subscription for that time.",
	},
	billing: {
		id: "provisioning.renew.billing",
		defaultMessage: "Yoast does not charge the customer for this. Only renew here when the subscription has also been renewed in your billing platform, so both stay in step.",
	},
	cancel: {
		id: "provisioning.renew.cancel",
		defaultMessage: "Cancel",
	},
	confirm: {
		id: "provisioning.renew.confirm",
		defaultMessage: "Extend with one billing term",
	},
	error: {
		id: "provisioning.renew.error",
		defaultMessage: "Could not renew the subscription. Please try again.",
	},
} );

/**
 * Confirms renewing a provisioned subscription.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function RenewProvisionedSubscriptionModal(
	{
		subscription = null,
		isSubmitting = false,
		hasError = false,
		errorMessage = null,
		onCancel,
		onConfirm,
		intl,
	},
) {
	return (
		<Modal
			isOpen={ Boolean( subscription ) }
			onClose={ onCancel }
			role="alertdialog"
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<Modal.Title as="h2" size="2">
					<FormattedMessage
						{ ...messages.title }
						values={ { number: subscription ? subscription.subscriptionNumber : "" } }
					/>
				</Modal.Title>
				<div className={ styles.modalBody }>
					<p>
						<FormattedMessage
							{ ...messages.body }
							values={ { email: <strong>{ subscription ? subscription.customerEmail : "" }</strong> } }
						/>
					</p>
					<p className={ styles.irreversible }>
						<FormattedMessage { ...messages.billing } />
					</p>
					{ hasError && (
						<Alert variant="error">
							{ errorMessage || <FormattedMessage { ...messages.error } /> }
						</Alert>
					) }
				</div>
				<div className={ styles.modalActions }>
					<Button variant="secondary" onClick={ onCancel } disabled={ isSubmitting }>
						<FormattedMessage { ...messages.cancel } />
					</Button>
					<Button
						variant="primary"
						onClick={ onConfirm }
						isLoading={ isSubmitting }
						disabled={ isSubmitting }
					>
						<FormattedMessage { ...messages.confirm } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

RenewProvisionedSubscriptionModal.propTypes = {
	subscription: provisionedSubscriptionShape,
	isSubmitting: PropTypes.bool,
	hasError: PropTypes.bool,
	errorMessage: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( RenewProvisionedSubscriptionModal );
